import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';

const API = '/api/admin/widget';
const TOKEN = () => localStorage.getItem('token');
const FIELD_TYPES = ['text', 'email', 'tel', 'number', 'select', 'textarea', 'checkbox'];

function authFetch(url, opts = {}) {
  return fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${TOKEN()}`,
      ...(opts.headers || {}),
    },
  }).then(r => r.json().then(d => ({ ok: r.ok, data: d })));
}

/* ── Reusable UI atoms ─────────────────────────────────────────────────────── */
function Input({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{label}</label>}
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, color: '#111827', outline: 'none' }}
      />
    </div>
  );
}

function Pill({ color, children }) {
  const tones = {
    green:  { bg: '#d1fae5', text: '#065f46' },
    blue:   { bg: '#dbeafe', text: '#1e40af' },
    purple: { bg: '#ede9fe', text: '#5b21b6' },
    amber:  { bg: '#fef3c7', text: '#92400e' },
  };
  const t = tones[color] || tones.blue;
  return (
    <span style={{ display: 'inline-block', background: t.bg, color: t.text, borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
      {children}
    </span>
  );
}

function CodeBlock({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div style={{ position: 'relative', marginBottom: 8 }}>
      <pre style={{
        background: '#1e293b', color: '#e2e8f0', borderRadius: 10, padding: '16px 16px 14px',
        fontSize: 12.5, lineHeight: 1.7, overflowX: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
      }}>{value}</pre>
      <button
        onClick={copy}
        style={{
          position: 'absolute', top: 10, right: 10,
          background: copied ? '#10b981' : '#334155', color: '#fff',
          border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12,
          cursor: 'pointer', transition: 'background .2s',
        }}
      >
        {copied ? '✓ Copied' : 'Copy'}
      </button>
    </div>
  );
}

/* ── TABS ──────────────────────────────────────────────────────────────────── */
const TABS = [
  { key: 'setup',    label: 'Setup' },
  { key: 'form',     label: 'Form Builder' },
  { key: 'settings', label: 'Settings' },
  { key: 'preview',  label: 'Preview' },
];

/* ── Main page ─────────────────────────────────────────────────────────────── */
export default function WidgetPage() {
  const [activeTab, setActiveTab] = useState('setup');
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [data, setData]           = useState(null);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [snippetMode, setSnippetMode] = useState('inline');

  // Editable state
  const [notifEmail, setNotifEmail]   = useState('');
  const [title, setTitle]             = useState('Get in Touch');
  const [btnText, setBtnText]         = useState('Send Enquiry');
  const [successTxt, setSuccessTxt]   = useState("Thank you! We'll be in touch soon.");
  const [fields, setFields]           = useState([]);
  const [customFields, setCustomFields] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(API);
      if (res.ok) {
        const d = res.data;
        setData(d);
        setNotifEmail(d.notificationEmail || '');
        setTitle(d.title || 'Get in Touch');
        setBtnText(d.submitButtonText || 'Send Enquiry');
        setSuccessTxt(d.successMessage || "Thank you! We'll be in touch soon.");
        setFields(d.fields || []);
        setCustomFields(d.customFields || []);
      } else {
        setError(res.data?.error || 'Failed to load widget settings');
      }
    } catch {
      setError('Failed to load widget settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await authFetch(API, {
        method: 'PATCH',
        body: JSON.stringify({
          notification_email: notifEmail,
          title,
          submit_button_text: btnText,
          success_message: successTxt,
          fields,
          custom_fields: customFields,
        }),
      });
      if (res.ok) {
        setSuccess('Widget settings saved.');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(res.data?.error || 'Save failed');
      }
    } catch {
      setError('Save failed');
    } finally {
      setSaving(false);
    }
  };

  /* ── Field editor helpers ──────────────────────────────────────────────── */
  const updateSystemField = (idx, key, val) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, [key]: val } : f));
  };

  const toggleSystemField = (idx) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, enabled: !f.enabled } : f));
  };

  const addCustomField = () => {
    const id = 'cf_' + Date.now();
    setCustomFields(prev => [...prev, { id, type: 'text', label: '', required: false }]);
  };

  const updateCustomField = (idx, key, val) => {
    setCustomFields(prev => prev.map((f, i) => i === idx ? { ...f, [key]: val } : f));
  };

  const removeCustomField = (idx) => {
    setCustomFields(prev => prev.filter((_, i) => i !== idx));
  };

  const moveCustomField = (idx, dir) => {
    setCustomFields(prev => {
      const arr = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return arr;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return arr;
    });
  };

  /* ── Render ─────────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="dashboard-container">
        <Sidebar />
        <main className="dashboard-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#6b7280' }}>Loading…</span>
        </main>
      </div>
    );
  }

  const snippet = snippetMode === 'inline' ? data?.snippet : data?.snippetFloat;

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main" style={{ padding: '32px 40px', maxWidth: 860 }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0e1b2c' }}>Website Widget</h1>
            <Pill color="green">Active</Pill>
          </div>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>
            Embed a contact form on your website — leads flow directly into BrokerConnect.
          </p>
        </div>

        {error   && <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>{error}</div>}
        {success && <div style={{ background: '#d1fae5', color: '#065f46', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>{success}</div>}

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '2px solid #EDF2F7', marginBottom: 28, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                background: 'none', border: 'none', padding: '10px 20px', fontSize: 14, cursor: 'pointer',
                fontWeight: activeTab === tab.key ? 700 : 500,
                color: activeTab === tab.key ? '#0e1b2c' : '#718096',
                borderBottom: activeTab === tab.key ? '3px solid #10b981' : '3px solid transparent',
                marginBottom: -2, whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── TAB: Setup ─────────────────────────────────────────────────── */}
        {activeTab === 'setup' && (
          <div>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c', marginBottom: 4 }}>Your widget API key</div>
              <div style={{ fontFamily: 'monospace', fontSize: 14, color: '#6b7280', wordBreak: 'break-all' }}>{data?.apiKey}</div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#374151', marginBottom: 10 }}>Embed mode</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {[
                  { key: 'inline', label: '📋 Inline form', desc: 'Form embedded directly in a page section' },
                  { key: 'float',  label: '💬 Floating button', desc: 'Chat-style button in the corner of every page' },
                ].map(m => (
                  <div
                    key={m.key}
                    onClick={() => setSnippetMode(m.key)}
                    style={{
                      flex: 1, padding: '14px 16px', border: `2px solid ${snippetMode === m.key ? '#10b981' : '#e2e8f0'}`,
                      borderRadius: 10, cursor: 'pointer', background: snippetMode === m.key ? '#f0fdf4' : '#fff',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 4 }}>{m.label}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{m.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 14, color: '#374151' }}>
              Paste this into your website HTML
            </div>
            {snippet && <CodeBlock value={snippet} />}

            {snippetMode === 'inline' && (
              <p style={{ fontSize: 13, color: '#6b7280', margin: '8px 0 0' }}>
                Place the <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>&lt;div id="bc-widget"&gt;</code> wherever you want the form to appear on the page. The script tag can go anywhere.
              </p>
            )}
            {snippetMode === 'float' && (
              <p style={{ fontSize: 13, color: '#6b7280', margin: '8px 0 0' }}>
                Paste this just before your closing <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>&lt;/body&gt;</code> tag. The button appears on every page automatically.
              </p>
            )}

            <div style={{ marginTop: 28, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#92400e', marginBottom: 6 }}>Optional: reduce spam even further with Cloudflare Turnstile</div>
              <p style={{ margin: 0, fontSize: 13, color: '#78350f' }}>
                The widget already blocks bots with a honeypot, time check, and rate limiting. For extra protection, add Cloudflare Turnstile (free): set up a site at <strong>dash.cloudflare.com → Turnstile</strong>, choose <em>"All hostnames"</em>, then add <code style={{ background: '#fef3c7', padding: '1px 6px', borderRadius: 4 }}>TURNSTILE_SITE_KEY</code> and <code style={{ background: '#fef3c7', padding: '1px 6px', borderRadius: 4 }}>TURNSTILE_SECRET_KEY</code> to your server .env.
              </p>
            </div>
          </div>
        )}

        {/* ── TAB: Form Builder ──────────────────────────────────────────── */}
        {activeTab === 'form' && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c', marginBottom: 4 }}>Default fields</div>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>Edit labels, toggle on/off, and set required. The field type cannot be changed on default fields.</p>

              {fields.map((f, idx) => (
                <div key={f.id} style={{
                  border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 10,
                  background: f.enabled ? '#fff' : '#f9fafb', opacity: f.enabled ? 1 : 0.6,
                }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    {/* Label input */}
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Field label</label>
                      <input
                        type="text"
                        value={f.label}
                        onChange={e => updateSystemField(idx, 'label', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #d1d5db', borderRadius: 7, fontSize: 13, color: '#111827', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                    {/* Type pill (read-only) */}
                    <div style={{ paddingBottom: 8 }}>
                      <Pill color={f.type === 'select' ? 'purple' : f.type === 'textarea' ? 'amber' : 'blue'}>{f.type}</Pill>
                    </div>
                  </div>
                  {/* Options editor for select-type system fields */}
                  {f.type === 'select' && (
                    <div style={{ marginTop: 10 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Options (one per line)</label>
                      <textarea
                        value={(f.options || []).join('\n')}
                        onChange={e => updateSystemField(idx, 'options', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
                        rows={5}
                        style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #d1d5db', borderRadius: 7, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                      />
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 10, flexWrap: 'wrap' }}>
                    {/* Enable toggle */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer', color: '#374151', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={f.enabled}
                        onChange={() => toggleSystemField(idx)}
                        style={{ accentColor: '#10b981', width: 15, height: 15 }}
                      />
                      Show in form
                    </label>
                    {/* Required toggle */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer', color: '#374151', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={f.required || false}
                        onChange={e => updateSystemField(idx, 'required', e.target.checked)}
                        style={{ accentColor: '#10b981', width: 15, height: 15 }}
                      />
                      Required
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 24, marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c' }}>Custom fields</div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Add your own questions — answers go into the lead record and notification email.</div>
                </div>
                <button
                  onClick={addCustomField}
                  style={{ background: '#0e1b2c', color: '#10b981', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  + Add field
                </button>
              </div>

              {customFields.length === 0 && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: 14, border: '1.5px dashed #e2e8f0', borderRadius: 8 }}>
                  No custom fields yet — click "Add field" to create one.
                </div>
              )}

              {customFields.map((f, idx) => (
                <div key={f.id} style={{
                  border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px', marginBottom: 12, background: '#fff',
                }}>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: 2, minWidth: 180 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Field label</label>
                      <input
                        type="text"
                        value={f.label}
                        onChange={e => updateCustomField(idx, 'label', e.target.value)}
                        placeholder="e.g. Approximate property value"
                        style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #d1d5db', borderRadius: 7, fontSize: 13 }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 120 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Type</label>
                      <select
                        value={f.type}
                        onChange={e => updateCustomField(idx, 'type', e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #d1d5db', borderRadius: 7, fontSize: 13 }}
                      >
                        {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>

                  {f.type === 'select' && (
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
                        Options (one per line)
                      </label>
                      <textarea
                        value={(f.options || []).join('\n')}
                        onChange={e => updateCustomField(idx, 'options', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
                        rows={4}
                        placeholder="Under 5%&#10;5–10%&#10;10–20%&#10;20%+"
                        style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #d1d5db', borderRadius: 7, fontSize: 13, resize: 'vertical' }}
                      />
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: '#374151' }}>
                      <input
                        type="checkbox"
                        checked={f.required || false}
                        onChange={e => updateCustomField(idx, 'required', e.target.checked)}
                        style={{ accentColor: '#10b981' }}
                      />
                      Required
                    </label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => moveCustomField(idx, -1)} disabled={idx === 0}
                        style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }} title="Move up">↑</button>
                      <button onClick={() => moveCustomField(idx, 1)} disabled={idx === customFields.length - 1}
                        style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }} title="Move down">↓</button>
                      <button onClick={() => removeCustomField(idx)}
                        style={{ background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button onClick={save} disabled={saving}
              style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save form'}
            </button>
          </div>
        )}

        {/* ── TAB: Settings ──────────────────────────────────────────────── */}
        {activeTab === 'settings' && (
          <div style={{ maxWidth: 520 }}>
            <Input
              label="Notification email"
              value={notifEmail}
              onChange={setNotifEmail}
              type="email"
              placeholder="e.g. enquiries@yourfirm.co.uk"
            />
            <p style={{ margin: '-10px 0 20px', fontSize: 12, color: '#9ca3af' }}>
              Every widget submission is emailed here as well as stored in BrokerConnect. Can be a shared inbox.
            </p>

            <Input label="Widget title" value={title} onChange={setTitle} placeholder="Get in Touch" />
            <Input label="Submit button text" value={btnText} onChange={setBtnText} placeholder="Send Enquiry" />

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Success message</label>
              <textarea
                value={successTxt}
                onChange={e => setSuccessTxt(e.target.value)}
                rows={3}
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, color: '#111827', resize: 'vertical' }}
                placeholder="Thank you! We'll be in touch soon."
              />
            </div>

            <button onClick={save} disabled={saving}
              style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        )}

        {/* ── TAB: Preview ───────────────────────────────────────────────── */}
        {activeTab === 'preview' && (
          <div>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
              This is a visual preview of how your widget form will look. The live widget uses your saved settings.
            </p>
            <div style={{
              border: '1px solid #e2e8f0', borderRadius: 12, padding: 28, background: '#f8fafc',
              maxWidth: 480,
            }}>
              <div style={{ fontWeight: 700, fontSize: 20, color: '#111827', marginBottom: 20 }}>{title}</div>
              {[...fields.filter(f => f.enabled), ...customFields].map(f => (
                <div key={f.id} style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                    {f.label}{f.required && <span style={{ color: '#10b981' }}> *</span>}
                  </label>
                  {f.type === 'textarea' ? (
                    <textarea rows={3} disabled placeholder=""
                      style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, background: '#fff', resize: 'none' }} />
                  ) : f.type === 'select' ? (
                    <select disabled style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, background: '#fff' }}>
                      <option>Please select…</option>
                      {(f.options || []).map(o => <option key={o}>{o}</option>)}
                    </select>
                  ) : f.type === 'checkbox' ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'default' }}>
                      <input type="checkbox" disabled /> <span style={{ fontSize: 14, color: '#374151' }}>{f.label}</span>
                    </label>
                  ) : (
                    <input type={f.type} disabled style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, background: '#fff' }} />
                  )}
                </div>
              ))}
              <button disabled style={{
                width: '100%', padding: 12, background: '#10b981', color: '#fff', border: 'none',
                borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'not-allowed', opacity: 0.85,
              }}>
                {btnText}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
